import { useEffect, useMemo, useState } from 'react'
import {
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  Database,
  Download,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  Handshake,
  Menu,
  Search,
  Users,
  X,
} from 'lucide-react'
import CreatorExplorer from './components/CreatorExplorer.jsx'
import LiquidGlassSurface from './components/LiquidGlassSurface.jsx'
import ResourceCharts from './components/ResourceCharts.jsx'
import {
  LOCAL_WORKBOOK_URL,
  excellentCases,
  headCreators,
  historyData,
  j200Data,
  koreaReference,
  methodologyNotes,
  navSections,
  overviewMetrics,
  performanceTiers,
  poolData,
  reportMeta,
} from './data/reportData.js'

const integerFormatter = new Intl.NumberFormat('zh-CN')
const yenFormatter = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 })
const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function formatNumber(value) {
  return integerFormatter.format(value)
}

function formatW(value, digits = 1) {
  if (value >= 10000) return `${(value / 10000).toFixed(digits).replace(/\.0$/, '')}W`
  return formatNumber(value)
}

function compactNumber(value) {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1).replace(/\.0$/, '')}亿`
  return formatW(value)
}

function formatFee(value, currency, compact = false) {
  if (currency === 'USD') return usdFormatter.format(value)
  if (compact && value >= 10000) return `¥${formatW(value)}`
  return yenFormatter.format(value)
}

function usePageNavigation() {
  const [active, setActive] = useState('overview')
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const sections = navSections.map(({ id }) => document.getElementById(id)).filter(Boolean)
    let frame = 0
    const updateProgress = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        const distance = document.documentElement.scrollHeight - window.innerHeight
        setProgress(distance > 0 ? Math.min(1, window.scrollY / distance) : 0)
        const readingLine = window.scrollY + 180
        const current = sections.reduce((match, section) => (
          section.offsetTop <= readingLine ? section : match
        ), sections[0])
        if (current) setActive(current.id)
        frame = 0
      })
    }
    updateProgress()
    window.addEventListener('scroll', updateProgress, { passive: true })
    return () => {
      window.removeEventListener('scroll', updateProgress)
      window.cancelAnimationFrame(frame)
    }
  }, [])

  return { active, progress }
}

function useReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('[data-reveal]')
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'))
      return undefined
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        observer.unobserve(entry.target)
      }
    }), { threshold: 0.06, rootMargin: '0px 0px -6% 0px' })
    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])
}

function TopNavigation() {
  const { active, progress } = usePageNavigation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const close = () => window.innerWidth > 900 && setOpen(false)
    window.addEventListener('resize', close)
    return () => window.removeEventListener('resize', close)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const closeOnEscape = (event) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])

  return (
    <>
      <LiquidGlassSurface preset="nav" cornerRadius={24} className="top-navigation">
        <a className="top-brand" href="#overview" aria-label="回到报告顶部">Aqara</a>
        <nav className="desktop-navigation" aria-label="报告章节">
          {navSections.map((section) => (
            <a key={section.id} className={active === section.id ? 'active' : ''} href={`#${section.id}`}>{section.label}</a>
          ))}
        </nav>
        <div className="period-control">{reportMeta.period}</div>
        <button className="nav-toggle" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label={open ? '关闭导航' : '打开导航'}>
          {open ? <X size={19} /> : <Menu size={19} />}
        </button>
        <span className="nav-progress" aria-hidden="true"><i style={{ transform: `scaleX(${progress})` }} /></span>
      </LiquidGlassSurface>
      {open ? (
        <nav className="mobile-navigation open" aria-label="移动端报告章节">
          {navSections.map((section) => (
            <a key={section.id} href={`#${section.id}`} onClick={() => setOpen(false)}>{section.label}<ChevronRight size={17} /></a>
          ))}
        </nav>
      ) : null}
    </>
  )
}

function SectionHeader({ title, note, aside, kicker }) {
  return (
    <header className="section-heading" data-reveal>
      <div>
        {kicker ? <span className="section-kicker">{kicker}</span> : null}
        <h2>{title}</h2>
        {note ? <p>{note}</p> : null}
      </div>
      {aside ? <div className="section-aside">{aside}</div> : null}
    </header>
  )
}

function HeroSection() {
  return (
    <section id="overview" className="report-section hero-section">
      <div className="page-shell hero-shell">
        <div className="hero-title" data-reveal>
          <span className="hero-eyebrow">日本市场 · 达人资源报告</span>
          <h1><span>Aqara</span>{' '}<span>日本达人资源盘点</span></h1>
          <div className="hero-date"><span>{reportMeta.period}</span><i /><span>数据更新 {reportMeta.updatedAt}</span></div>
        </div>
        <div className="hero-metric-tray" data-reveal>
          {overviewMetrics.map((metric) => (
            <a key={metric.id} href={metric.target} className={`hero-metric ${metric.accent ? 'accent' : ''}`}>
              <span>{metric.label}</span>
              <strong>{formatNumber(metric.value)}</strong>
              <small>{metric.note}</small>
            </a>
          ))}
        </div>
        <p className="hero-note" data-reveal>{reportMeta.note}</p>
      </div>
    </section>
  )
}

function PoolSection() {
  const quickStats = [
    { label: '总资源池', value: 710, note: '去重身份', icon: Database },
    { label: '在库达人', value: 113, note: '历史记录资源', icon: Users },
    { label: '外部初筛达人', value: 597, note: '第三方平台筛选', icon: Search },
  ]
  return (
    <section id="pool" className="report-section pool-section">
      <div className="page-shell">
        <SectionHeader title="资源池概况" note="达人资源包括历史有记录的达人，以及从第三方平台新筛选的达人；下方集中展示资源规模、结构与品牌关系。" />
        <div className="pool-quick-stats" data-reveal>
          {quickStats.map((item) => {
            const Icon = item.icon
            return <article key={item.label}><Icon size={21} /><span>{item.label}</span><strong>{formatNumber(item.value)}</strong><small>{item.note}</small></article>
          })}
        </div>
        <ResourceCharts data={poolData} />
        <aside className="market-characteristics" data-reveal aria-labelledby="market-characteristics-title">
          <header>
            <span>日本科技 KOL 市场观察</span>
            <h3 id="market-characteristics-title">资源结构与传播特征</h3>
          </header>
          <ul>
            <li><strong>中腰部资源充足，头部科技达人稀缺。</strong></li>
            <li><strong>达人内容比较垂直、分散</strong><span>Apple、PC、家电、DIY、生活方式各自形成小圈层。</span></li>
            <li><strong>单个达人破圈能力偏弱</strong><span>跨平台和跨圈层传播不如中国、欧美。</span></li>
            <li><strong>内容更偏长期评测和信任积累</strong><span>短期爆量能力通常不如其他区域达人。</span></li>
            <li><strong>受众价值不低</strong><span>日本用户购买力和对专业评测的信任度较高，不能只用播放量判断价值。</span></li>
          </ul>
        </aside>
        <p className="section-footnote pool-source-note">
          外部初筛达人来自 NoxInfluencer 日本地区 YouTube／Instagram／TikTok，以智能门锁、智能家居、Apple 生态和生活方式等主题筛选。
        </p>
      </div>
    </section>
  )
}

function HistorySection() {
  return (
    <section id="history" className="report-section history-section">
      <div className="page-shell">
        <SectionHeader title="历史合作概况" note="2024、2025 为历史 tracker；2026 为 J200 当前合作。J200 费用统一换算为 USD 展示。" />
        <div className="history-timeline" data-reveal>
          {historyData.years.map((year) => (
            <article key={year.year} className={year.year === '2026' ? 'current' : ''}>
              <header><span>{year.year}</span><small>{year.label}</small></header>
              <div className="history-primary">
                <div><span>合作达人</span><strong>{year.creators}</strong><small>位</small></div>
                <div><span>发布／记录</span><strong>{year.publications}</strong><small>条</small></div>
              </div>
              <dl className="history-detail-list">
                <div><dt>付费 / 置换</dt><dd>{year.paid} / {year.exchange}{year.unknown ? ` / 待补 ${year.unknown}` : ''}</dd></div>
                <div><dt>记录费用</dt><dd>{year.feeLabel ?? formatFee(year.fee, year.currency, true)}</dd></div>
                <div><dt>总曝光</dt><dd>{formatW(year.views)}</dd></div>
                <div><dt>平均曝光</dt><dd>{formatW(year.avgViews)}</dd></div>
                <div><dt>最高曝光</dt><dd>{formatW(year.maxViews)} · {year.peakCreator}</dd></div>
                <div><dt>曝光覆盖</dt><dd>{year.coverage}</dd></div>
              </dl>
            </article>
          ))}
        </div>
        <div className="performance-table-wrap" data-reveal>
          <header><h3>粉丝层级 · 表现与费用</h3><p>历史费用来自 2024—2025 合作记录；外部人数与播放来自第三方平台初筛池。</p></header>
          <div className="table-scroll">
            <table className="performance-table">
              <thead><tr><th>粉丝量级</th><th>历史达人</th><th>历史均播</th><th>历史中位</th><th>历史平均费用</th><th>外部达人</th><th>外部均播</th><th>外部中位</th></tr></thead>
              <tbody>{performanceTiers.map((tier) => (
                <tr key={tier.label}>
                  <th>{tier.label}</th><td>{tier.historyCreators}</td><td>{formatNumber(tier.historyAvg)}</td><td>{formatNumber(tier.historyMedian)}</td><td>{yenFormatter.format(tier.historyFee)}</td>
                  <td>{tier.externalCreators}</td><td>{formatNumber(tier.externalAvg)}</td><td>{formatNumber(tier.externalMedian)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}

function J200Section() {
  const [launchType, setLaunchType] = useState('paid')
  const launchCreators = launchType === 'paid' ? j200Data.launch.paidCreators : j200Data.launch.exchangeCreators

  return (
    <section id="j200" className="report-section j200-section">
      <div className="page-shell">
        <SectionHeader title="J200 当前合作" note="众筹与正式上市统一按 2026 当前有效合作统计；另有 7 位失注／暂停未计入。" aside={<span className="live-status"><i />{j200Data.status}</span>} />
        <div className="j200-stage-grid" data-reveal>
          <article className="j200-total-card">
            <span>当前有效合作</span><strong>{j200Data.effectiveCreators}</strong><small>位达人</small>
            <div className="j200-composition-bar">{j200Data.composition.map((item) => <i key={item.label} style={{ flex: item.value, background: item.color }} title={`${item.label} ${item.value}`} />)}</div>
            <ul>{j200Data.composition.map((item) => <li key={item.label}><i style={{ background: item.color }} /><span>{item.label}</span><b>{item.value}</b></li>)}</ul>
          </article>
          <article className="j200-launch-card">
            <span>正式上市期</span><strong>{j200Data.launch.creators}</strong><small>位达人</small>
            <div className="launch-breakdown" aria-label="正式上市合作类型">
              <button type="button" className={launchType === 'paid' ? 'active' : ''} aria-pressed={launchType === 'paid'} onClick={() => setLaunchType('paid')}>付费 {j200Data.launch.paid}</button>
              <button type="button" className={launchType === 'exchange' ? 'active' : ''} aria-pressed={launchType === 'exchange'} onClick={() => setLaunchType('exchange')}>置换 {j200Data.launch.exchange}</button>
              <b>{formatW(j200Data.launch.exposure)} 曝光</b>
            </div>
            <ul className="launch-creator-list" aria-live="polite">{launchCreators.map((creator) => <li key={creator.name}><a href={creator.url} target="_blank" rel="noreferrer"><span>{creator.name}<small>{creator.contents} 条内容</small></span><b>{formatNumber(creator.views)} 播放</b><ExternalLink size={13} /></a></li>)}</ul>
            <p>{j200Data.launch.period} · {launchType === 'paid' ? '按当前播放／浏览从高到低' : '置换现金费用按 0 记录'}</p>
          </article>
        </div>
        <div className="j200-phase-summary-grid" data-reveal>
          <article className="j200-phase-summary">
            <header><span>众筹期</span><strong>{j200Data.crowdfunding.creators}<small> 位达人</small></strong></header>
            <div className="phase-summary-metrics">
              <div><Handshake size={18} /><span>合作方式</span><strong>付费 {j200Data.crowdfunding.paid} · 置换 {j200Data.crowdfunding.exchange}</strong><small>无费用记录均按置换统计</small></div>
              <div><span className="metric-icon">$</span><span>已确认费用</span><strong>{usdFormatter.format(j200Data.crowdfunding.confirmedFeeUsd)}</strong><small>{j200Data.crowdfunding.confirmedPayments} 笔 · 按 ¥150 / USD 换算</small></div>
              <div><Eye size={18} /><span>记录总曝光</span><strong>{formatW(j200Data.crowdfunding.exposure)}</strong><small>覆盖 {j200Data.crowdfunding.exposureCoverage} / {j200Data.crowdfunding.creators}</small></div>
              <div><CheckCircle2 size={18} /><span>已知记录均值</span><strong>{formatW(j200Data.crowdfunding.averageViews)}</strong><small>最高 {formatW(j200Data.crowdfunding.maxViews)} · {j200Data.crowdfunding.maxCreator}</small></div>
            </div>
          </article>
          <article className="j200-phase-summary">
            <header><span>正式上市期</span><strong>{j200Data.launch.creators}<small> 位达人</small></strong></header>
            <div className="phase-summary-metrics">
              <div><Handshake size={18} /><span>合作方式</span><strong>付费 {j200Data.launch.paid} · 置换 {j200Data.launch.exchange}</strong><small>合作方式已全部确认</small></div>
              <div><span className="metric-icon">$</span><span>已确认费用</span><strong>{usdFormatter.format(j200Data.launch.confirmedFeeUsd)}</strong><small>{j200Data.launch.confirmedPayments} 笔现金费用</small></div>
              <div><Eye size={18} /><span>记录总曝光</span><strong>{formatW(j200Data.launch.exposure)}</strong><small>覆盖 {j200Data.launch.exposureCoverage} / {j200Data.launch.creators}</small></div>
              <div><CheckCircle2 size={18} /><span>已知记录均值</span><strong>{formatW(j200Data.launch.averageViews)}</strong><small>最高 {formatW(j200Data.launch.maxViews)} · {j200Data.launch.maxCreator}</small></div>
            </div>
          </article>
        </div>
        <p className="section-footnote">费用核对：众筹期来自 J200 PR list 的已确认费用及 Aqara 侧已确认记录；正式上市期来自【有道×绿米】执行表。询价区间、样机和物流成本不计入；众筹期日元费用按固定汇报汇率 1 USD = 150 JPY 换算，页面统一以 USD 展示。15 / 37 位尚无曝光回填。</p>
      </div>
    </section>
  )
}

function CasesSection() {
  return (
    <section id="cases" className="report-section cases-section">
      <div className="page-shell">
        <SectionHeader title="优秀合作案例" note="按曝光、成本与场景代表性选取 6 个样本；点击卡片可打开原内容。" />
        <div className="case-grid">
          {excellentCases.map((item) => (
            <a className="case-card" href={item.url} target="_blank" rel="noreferrer" key={item.id} data-reveal>
              <div className="case-media"><img className={item.imageClass || ''} src={item.image} alt={`${item.creator} 合作内容缩略图`} loading="lazy" /><span>{item.platform}</span><b><ExternalLink size={15} /> 原内容</b></div>
              <div className="case-copy">
                <span>{item.year} · {item.project}</span>
                <h3>{item.title}</h3>
                <p className="case-creator">{item.creator}</p>
                <p className="case-insight">{item.insight}</p>
              </div>
              <div className="case-metrics"><span><small>曝光</small><strong>{formatW(item.exposure)}</strong></span><span><small>费用</small><strong>{item.feeLabel || (item.cooperationType === '置换' ? '置换' : formatFee(item.fee, item.currency, true))}</strong></span></div>
            </a>
          ))}
        </div>
        <p className="section-footnote">历史案例费用来自 tracker 实际记录；J200 案例费用来自本地管理表已确认口径。</p>
      </div>
    </section>
  )
}

function CreatorsSection() {
  return (
    <section id="creators" className="report-section creators-section">
      <div className="page-shell">
        <SectionHeader title="后期可考虑合作资源" note="从总资源池中按品类适配度、内容表现、受众与商业合作记录筛出 30 个平台账号，跨平台合并为 28 位达人；默认优先展示历史或 J200 已有合作关系。" />
        <CreatorExplorer />
      </div>
    </section>
  )
}

function CreatorAvatar({ creator }) {
  const [failed, setFailed] = useState(false)
  if (failed || !creator.avatar) return <span className="avatar-fallback">{[...creator.name][0]}</span>
  return <img src={creator.avatar} alt="" loading="lazy" onError={() => setFailed(true)} />
}

function HeadCard({ creator }) {
  return (
    <article className="head-card" data-reveal>
      <header>
        <CreatorAvatar creator={creator} />
        <div><span>{creator.recommendation}</span><h3>{creator.name}</h3><small>{creator.relationship}</small></div>
        <a href={creator.url} target="_blank" rel="noreferrer" aria-label={`打开 ${creator.name} 主页`}><ExternalLink size={18} /></a>
      </header>
      <p>{creator.description}</p>
      <div className="head-metrics">
        <div><span>粉丝</span><strong>{compactNumber(creator.followers)}</strong></div>
        <div><span>均播</span><strong>{compactNumber(creator.avgViews)}</strong></div>
        <div><span>中位数</span><strong>{compactNumber(creator.medianViews)}</strong></div>
      </div>
      <div className="head-price"><span>预算预估<small>{creator.quoteStatus}</small></span><strong>{creator.budgetRange}</strong></div>
      <div className="head-evidence">
        <span>智能家居相关记录</span>
        {creator.evidence.length ? <ul>{creator.evidence.map((item, index) => <li key={`${item.brand}-${index}`}><b>{item.brand}</b><span>最高 {formatW(item.views)}</span></li>)}</ul> : <p>公开补抓暂未检出目标合作证据，需在询价前继续人工确认。</p>}
      </div>
      <p className="budget-note">内部预算预估，仅用于询价前规划；品牌内容均播 {formatNumber(creator.brandAvgViews)}，最终以达人正式报价为准。</p>
    </article>
  )
}

function HeadSection() {
  return (
    <section id="head" className="report-section head-section">
      <div className="page-shell">
        <SectionHeader title="头部达人" note="按主页内容、粉丝与曝光表现、智能家居证据及 Aqara 适配度排序；全部需要正式询价。" />
        <div className="head-grid">{headCreators.map((creator) => <HeadCard key={creator.id} creator={creator} />)}</div>
        <article className="korea-reference" data-reveal>
          <header className="korea-reference-header">
            <a className="korea-reference-profile" href={koreaReference.url} target="_blank" rel="noreferrer">
              <img src={koreaReference.avatar} alt={`${koreaReference.name} 频道头像`} />
              <span><small>韩国市场参考</small><strong>{koreaReference.name}</strong><b>{compactNumber(koreaReference.followers)} 粉丝</b></span>
              <ExternalLink size={16} />
            </a>
            <div className="korea-reference-budget"><span>合作预算</span><strong>{koreaReference.budget}</strong><small>用户提供历史案例口径</small></div>
          </header>
          <div className="korea-reference-copy"><h3>{koreaReference.title}</h3><p>{koreaReference.description}</p></div>
          <div className="korea-video-grid">
            {koreaReference.videos.map((video) => (
              <a href={video.url} target="_blank" rel="noreferrer" key={video.url}>
                <img src={video.image} alt={`${video.product} 合作视频封面`} />
                <span><small>{video.product}</small><strong>{video.title}</strong><b>{formatW(video.views)} 播放 <ExternalLink size={13} /></b></span>
              </a>
            ))}
          </div>
        </article>
      </div>
    </section>
  )
}

function AppendixSection() {
  return (
    <section id="appendix" className="report-section appendix-section">
      <div className="page-shell">
        <SectionHeader title="数据附录" note="所有资源、历史合作、J200、后期可考虑合作资源与口径说明均合并在一个本地工作簿中。" />
        <a className="local-workbook-card" href={LOCAL_WORKBOOK_URL} download data-reveal>
          <span className="sheet-icon"><FileSpreadsheet size={28} /></span>
          <span><strong>Aqara 日本达人资源数据底表</strong><small>本地 Excel · 15 个子表 · 更新至 2026.07.22</small></span>
          <b><Download size={18} /> 下载工作簿</b>
        </a>
        <details className="methodology" data-reveal>
          <summary>口径与限制 <ChevronRight size={18} /></summary>
          <ol>{methodologyNotes.map((note) => <li key={note}>{note}</li>)}</ol>
        </details>
      </div>
    </section>
  )
}

export default function App() {
  useReveal()
  const footerYear = useMemo(() => new Date().getFullYear(), [])
  return (
    <>
      <a className="skip-link" href="#overview">跳到报告正文</a>
      <TopNavigation />
      <main>
        <HeroSection />
        <PoolSection />
        <HistorySection />
        <J200Section />
        <CasesSection />
        <CreatorsSection />
        <HeadSection />
        <AppendixSection />
      </main>
      <footer className="site-footer"><div className="page-shell"><span>Aqara 日本市场达人资源报告</span><span>{footerYear} · 管理层汇报材料</span></div></footer>
    </>
  )
}
